// src/oura/components/plan/PlanSaveBar.tsx
//
// The bar that floats at the foot of the Contract tab while the plan has
// unsaved changes (plan table spec v1.1, 8): how many, a one-line summary
// (the client sees it in the plan's history), Save plan and Discard.
// Discard asks first. A save that did not go through says why here, and the
// draft is kept.
//
// Presentational: ContractTab holds the draft, runs the save and passes the
// outcome in.

import { useEffect, useRef, useState, type RefObject } from "react";
import { cn } from "../../lib/cn";
import { LATE_TEXT, PillButton, SURFACE, TAP_TARGET, TYPE } from "@oura/ui";

export type SaveBarError =
    | { kind: "summary" }
    | { kind: "lines"; count: number; label: string }
    | { kind: "refused"; total: number; general: string[] }
    | { kind: "network" };

const INPUT = cn(
    TYPE.tb,
    "block h-9 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-rose-300",
);

function changesText(n: number): string {
    return `${n} ${n === 1 ? "change" : "changes"}`;
}

export function PlanSaveBar({
    count,
    summary,
    onSummary,
    busy,
    error,
    onSave,
    onDiscard,
    onShowLine,
    summaryRef,
}: {
    count: number;
    summary: string;
    onSummary: (text: string) => void;
    busy: boolean;
    error: SaveBarError | null;
    onSave: () => void;
    onDiscard: () => void;
    /** Opens the first line that needs fixing. */
    onShowLine: () => void;
    summaryRef: RefObject<HTMLInputElement | null>;
}) {
    const [confirming, setConfirming] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const wasConfirming = useRef(false);

    // Said once when the bar appears, not on every keystroke that changes the count.
    const [said, setSaid] = useState("");
    useEffect(() => {
        const id = window.setTimeout(() => setSaid(`${changesText(count)} not saved.`), 150);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const find = (slot: string) => rootRef.current?.querySelector<HTMLElement>(`[data-bar="${slot}"]`);
        if (confirming && !wasConfirming.current) find("keep")?.focus();
        if (!confirming && wasConfirming.current) find("discard")?.focus();
        wasConfirming.current = confirming;
    }, [confirming]);

    // A refusal is said where it can be read: on a phone the bar can sit under the keyboard or below the fold.
    useEffect(() => {
        if (!error) return;
        const t = window.setTimeout(() => rootRef.current?.scrollIntoView({ block: "nearest" }), 50);
        return () => window.clearTimeout(t);
    }, [error]);

    const errorText =
        error?.kind === "summary"
            ? "Say what changed, in one line."
            : error?.kind === "lines"
                ? error.count === 1
                    ? `Fix 1 line first: ${error.label}.`
                    : `Fix ${error.count} lines first. The first: ${error.label}.`
                : error?.kind === "refused"
                    ? `Not saved. ${error.total} ${error.total === 1 ? "problem" : "problems"}.`
                    : error?.kind === "network"
                        ? "Not saved. Oura could not be reached. Your changes are still here."
                        : null;

    return (
        <div
            ref={rootRef}
            role="region"
            aria-label="Unsaved plan changes"
            // Sticky rather than fixed: it floats at the foot of the window
            // while there is plan above it, centred on the content column,
            // so it never sits over the sidebar at a laptop width. On a phone
            // the column is the window less its 16px gutters.
            className={cn(SURFACE.card, "sticky bottom-6 z-30 mx-auto w-full max-w-[720px] px-4 py-3")}
        >
            <p className="sr-only" aria-live="polite">
                {said}
            </p>
            {confirming ? (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 text-gray-900")}>Discard {changesText(count)}?</p>
                    <div className="flex items-center gap-2">
                        <PillButton
                            variant="primary"
                            className={TAP_TARGET}
                            onClick={() => {
                                setConfirming(false);
                                onDiscard();
                            }}
                        >
                            Discard
                        </PillButton>
                        <PillButton data-bar="keep" className={TAP_TARGET} onClick={() => setConfirming(false)}>
                            Keep editing
                        </PillButton>
                    </div>
                </div>
            ) : (
                <form
                    noValidate
                    className="flex flex-wrap items-center gap-x-3 gap-y-2"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSave();
                    }}
                >
                    <span className={cn(TYPE.tc, "order-2 shrink-0 font-medium tabular-nums text-amber-700 sm:order-1")}>{changesText(count)}</span>
                    <input
                        ref={summaryRef}
                        type="text"
                        value={summary}
                        maxLength={200}
                        autoComplete="off"
                        aria-label="Summary of the change"
                        aria-invalid={error?.kind === "summary" ? true : undefined}
                        aria-describedby={errorText ? "plan-save-bar-error" : undefined}
                        placeholder="What changed? The client sees it."
                        onChange={(e) => onSummary(e.target.value)}
                        className={cn(INPUT, "order-1 min-w-0 flex-1 basis-full sm:order-2 sm:w-auto sm:basis-auto")}
                    />
                    <div className="order-3 ml-auto flex shrink-0 items-center gap-2">
                        <PillButton data-bar="discard" variant="text" disabled={busy} onClick={() => setConfirming(true)}>
                            Discard
                        </PillButton>
                        <PillButton type="submit" variant="primary" busy={busy} className={TAP_TARGET}>
                            Save plan
                        </PillButton>
                    </div>
                </form>
            )}
            {errorText && !confirming && (
                <div id="plan-save-bar-error" role="alert" className={cn(TYPE.tc, "mt-2 space-y-1 break-words")}>
                    <p className={cn(LATE_TEXT, "m-0")}>
                        {errorText}
                        {error?.kind === "lines" && (
                            <>
                                {" "}
                                <PillButton variant="text" onClick={onShowLine}>
                                    Show it
                                </PillButton>
                            </>
                        )}
                    </p>
                    {error?.kind === "refused" &&
                        error.general.map((g, i) => (
                            <p key={i} className="m-0 text-gray-700">
                                {g}
                            </p>
                        ))}
                </div>
            )}
        </div>
    );
}
